import { LinearProgress, Stack, Typography } from '@mui/material';
import { BlacklistScopes } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { Blacklist_New } from 'features/co-pilot/company-blacklists/company-blacklist.types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import BlacklistsTab from './blacklists-tab';
import GlobalBlacklists from './global-blacklists';
import LocalBlacklists from './local-blacklists';

interface Props {
  blacklistSyncLoading: boolean;
  activeBlacklistScope: BlacklistScopes;

  globalBlacklists: Blacklist_New[];
  localBlacklists: Blacklist_New[];
  globalBlacklistsLoading: boolean;
  localBlacklistsLoading: boolean;
  removeBlacklistItemsLoading: boolean;
  blacklists: string[];

  handleBlacklistScopeChange(newScope: BlacklistScopes): () => void;

  handleSelectedBlacklistsChange(blacklist: Blacklist_New): () => void;

  handleOpenAddLocalBlacklistDialog(): void;

  handleOpenEditBlacklistItemsDialog(blacklist: Blacklist_New): () => void;

  handleOpenEditBlacklistNameDialog(blacklist: Blacklist_New): () => void;

  handleOpenDeleteBlacklistDialog(blacklist: Blacklist_New): () => void;

  handleRemoveSingleBlacklistItem(blacklist: Blacklist_New): (identifier: string) => () => void;
}

const ManageBlacklists: React.FC<Props> = ({
  blacklistSyncLoading,
  activeBlacklistScope,
  globalBlacklists,
  localBlacklists,
  globalBlacklistsLoading,
  localBlacklistsLoading,
  removeBlacklistItemsLoading,
  blacklists,
  handleBlacklistScopeChange,
  handleSelectedBlacklistsChange,
  handleOpenAddLocalBlacklistDialog,
  handleOpenEditBlacklistItemsDialog,
  handleOpenEditBlacklistNameDialog,
  handleOpenDeleteBlacklistDialog,
  handleRemoveSingleBlacklistItem,
}) => {
  const { t } = useTranslation();

  /**
   * Tab content
   */

  const getTabContent = () => {
    switch (activeBlacklistScope) {
      case BlacklistScopes.GLOBAL:
        return (
          <GlobalBlacklists
            globalBlacklists={globalBlacklists}
            globalBlacklistsLoading={globalBlacklistsLoading}
            selectedGlobalBlacklists={blacklists}
            blacklistSyncLoading={blacklistSyncLoading}
            handleSelectedBlacklistsChange={handleSelectedBlacklistsChange}
          />
        );
      case BlacklistScopes.LOCAL:
        return (
          <LocalBlacklists
            localBlacklists={localBlacklists}
            localBlacklistsLoading={localBlacklistsLoading}
            removeBlacklistItemsLoading={removeBlacklistItemsLoading}
            selectedLocalBlacklists={blacklists}
            blacklistSyncLoading={blacklistSyncLoading}
            handleSelectedBlacklistsChange={handleSelectedBlacklistsChange}
            handleOpenAddLocalBlacklistDialog={handleOpenAddLocalBlacklistDialog}
            handleOpenEditBlacklistItemsDialog={handleOpenEditBlacklistItemsDialog}
            handleOpenEditBlacklistNameDialog={handleOpenEditBlacklistNameDialog}
            handleOpenDeleteBlacklistDialog={handleOpenDeleteBlacklistDialog}
            handleRemoveSingleBlacklistItem={handleRemoveSingleBlacklistItem}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <Stack gap={2} sx={{ height: '100%', overflow: 'hidden' }}>
        <BlacklistsTab
          activeBlacklistScope={activeBlacklistScope}
          totalGlobalBlacklists={globalBlacklists.length}
          totalLocalBlacklists={localBlacklists.length}
          handleBlacklistScopeChange={handleBlacklistScopeChange}
        />
        {blacklistSyncLoading && (
          <Stack gap={0.75}>
            <LinearProgress />
            <Typography variant={'caption'} color={'text.secondary'}>
              {t('waitingForChanges')}
            </Typography>
          </Stack>
        )}
        {getTabContent()}
      </Stack>
    </>
  );
};

export default ManageBlacklists;
